import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import {
    GlobalAccount,
    GUID,
    InternalUserProfile,
    Tenant,
    TenantTeamMember,
    TenantTeamMemberConfig,
    TenantTeamView,
} from '@ndvr/auth-api';
import type { Tenant as CustomerTenant } from '@ndvr/customer-api';

import { AuthApi, CustomerApi } from 'API';
import { useAuthContext } from 'views/auth/Auth';
import { useGlobalAccount } from 'views/household/household-queries';
import { Toast } from 'widgets';

const EMPTY_TENANT_TEAM: TenantTeamView = { advisors: [], clientSupport: [], legalAndCompliance: [] };

// TS mirror of TenantTeamView.allMembers() on the backend.
export const allTenantTeamMembers = (team?: TenantTeamView): TenantTeamMember[] => [
    ...(team?.advisors ?? []),
    ...(team?.clientSupport ?? []),
    ...(team?.legalAndCompliance ?? []),
];

export const TenantTeamQueryKeys = {
    tenants: ['tenantTeam', 'tenants'] as const,
    team: (tenantGUID?: GUID<Tenant>) => ['tenantTeam', tenantGUID, 'team'] as const,
    members: (tenantGUID?: GUID<Tenant>) => ['tenantTeam', tenantGUID, 'members'] as const,
};

export const useTenantListQuery = () =>
    useQuery<CustomerTenant[]>({
        queryKey: TenantTeamQueryKeys.tenants,
        queryFn: async () => {
            const [tenants] = await CustomerApi().getTenants();
            return tenants ?? [];
        },
    });

export const useTenantAdvisorsForTenant = (tenantGUID?: GUID<Tenant>) => {
    const { data: team, isPending } = useTenantTeamQuery(tenantGUID);
    return { advisors: team?.advisors ?? EMPTY_TENANT_TEAM.advisors, isPending: !!tenantGUID && isPending };
};

export const useTenantAdvisors = () => {
    const { currentTenant } = useAuthContext();
    return useTenantAdvisorsForTenant(currentTenant?.tenantGUID);
};

export const tenantTeamQueryOptions = (tenantGUID?: GUID<Tenant>) =>
    queryOptions({
        queryKey: TenantTeamQueryKeys.team(tenantGUID),
        enabled: !!tenantGUID,
        queryFn: async (): Promise<TenantTeamView> => {
            if (!tenantGUID) {
                return EMPTY_TENANT_TEAM;
            }
            const [team] = await AuthApi().getTenantTeam(tenantGUID);
            return team ?? EMPTY_TENANT_TEAM;
        },
    });

export const useTenantTeamQuery = (tenantGUID?: GUID<Tenant>) => useQuery(tenantTeamQueryOptions(tenantGUID));

export type TenantTeamNameResolver = (personOrgGUID?: string | null) => string | undefined;

/**
 * Resolves staff display names from the tenant team only, so names never leak across tenants.
 * An internal user's GUID shares its UUID with their PersonOrg GUID, so actor/author ids match directly.
 */
export const useTenantTeamNameResolver = (tenantGUID?: GUID<Tenant>) => {
    const { data: team, isPending } = useTenantTeamQuery(tenantGUID);
    const nameByGUID = useMemo(
        () => new Map(allTenantTeamMembers(team).map((member) => [String(member.internalUserGUID), member.name])),
        [team],
    );
    const nameFor = useCallback<TenantTeamNameResolver>(
        (personOrgGUID) => (personOrgGUID ? nameByGUID.get(personOrgGUID) : undefined),
        [nameByGUID],
    );
    return { nameFor, isPending: !!tenantGUID && isPending };
};

/**
 * {@link useTenantTeamNameResolver} for a record's own tenant, falling back to its household's tenant
 * when the record carries none.
 */
export const useRecordTeamNameResolver = ({
    tenantGUID,
    householdGUID,
}: {
    tenantGUID?: GUID<Tenant> | null;
    householdGUID?: GUID<GlobalAccount> | null;
}) => {
    const lookupHouseholdGUID = tenantGUID ? undefined : (householdGUID ?? undefined);
    const { data: household, isPending: isHouseholdPending } = useGlobalAccount(lookupHouseholdGUID);
    const resolver = useTenantTeamNameResolver(tenantGUID ?? household?.tenantGUID);
    return { ...resolver, isPending: (!!lookupHouseholdGUID && isHouseholdPending) || resolver.isPending };
};

/** {@link useTenantTeamNameResolver} for records that only carry their household. */
export const useHouseholdTeamNameResolver = (householdGUID?: GUID<GlobalAccount> | null) =>
    useRecordTeamNameResolver({ householdGUID });

export const useTenantTeamMemberCandidatesQuery = (tenantGUID?: GUID<Tenant>) =>
    useQuery<InternalUserProfile[]>({
        queryKey: TenantTeamQueryKeys.members(tenantGUID),
        enabled: !!tenantGUID,
        queryFn: async () => {
            if (!tenantGUID) {
                return [];
            }
            const [members] = await AuthApi().getUsersWithPermissionOnTenant(tenantGUID);
            return members ?? [];
        },
    });

export const useSaveTenantTeamMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            tenantGUID,
            members,
        }: {
            tenantGUID: GUID<Tenant>;
            members: TenantTeamMemberConfig[];
        }) => {
            const [response] = await AuthApi().upsertTenantTeam(tenantGUID, { members });
            return response ?? EMPTY_TENANT_TEAM;
        },
        onSuccess: (_response, variables) => {
            Toast.success('Tenant team saved.');
            queryClient.invalidateQueries({
                queryKey: TenantTeamQueryKeys.team(variables.tenantGUID),
            });
            queryClient.invalidateQueries({
                queryKey: TenantTeamQueryKeys.members(variables.tenantGUID),
            });
        },
        onError: (error: unknown) => {
            Toast.forError(error, 'Failed to save tenant team.');
        },
    });
};
